import { useState, useMemo, useEffect } from "react";
import { ThemeProvider, CircularProgress } from "@mui/material";
import { LocalizationProvider, MonthCalendar } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment";

import ModalSkeleton from "../../../Components/Skeleton/modal-skeleton";
import PrimaryButton from "../../../Components/Buttons/primary-button";

import { useViewport } from "../../../Contexts/ViewportContext";

import { useStakeDebit } from "../../../CustomHooks/Queries";
import { useDownloadStatement } from "../../../CustomHooks/Mutations";

import { dispatchAPIErrorToast } from "../../../Utils/helpers";
import { modalVariants } from "../../../Utils/constants";
import { theme } from "../../../Utils/theme";

import { ChevronLeft, ChevronRight } from "../../../Assets/Icons";

import "./bank-statements.scss";

export default function renderBankStatements(modalContext) {
  const { updateModalComponent, pushModalStack, setModalClassName, setModalTitle, setModalVariant } = modalContext;

  updateModalComponent(() => <BankStatements />);
  pushModalStack(() => renderBankStatements(modalContext));
  setModalTitle("Bank Statements");
  setModalVariant(modalVariants.fullScreenDialog);
  setModalClassName("bank-statements-modal");
}

function getDefaultStatementMonth(createdDate) {
  const currentMonth = moment().startOf("month");
  const previousMonth = moment().subtract(1, "month").startOf("month");
  const accountCreatedMonth = moment(createdDate).startOf("month");

  if (!accountCreatedMonth.isValid()) {
    return previousMonth;
  }

  // Account opened this month — no statement for the prior month
  if (accountCreatedMonth.isSameOrAfter(currentMonth)) {
    return currentMonth;
  }

  if (previousMonth.isBefore(accountCreatedMonth)) {
    return accountCreatedMonth;
  }

  return previousMonth;
}

const BankStatements = () => {
  const [selectedDate, setSelectedDate] = useState(null);

  const { width } = useViewport();

  const { isLoading: isLoadingStakeDebit, data: stakeDebit } = useStakeDebit();

  useEffect(() => {
    if (isLoadingStakeDebit) return;

    const createdDate = stakeDebit?.data?.data?.created_date;
    setSelectedDate((prev) => {
      if (prev) return prev;
      return getDefaultStatementMonth(createdDate);
    });
  }, [isLoadingStakeDebit, stakeDebit]);

  const { isLoading: isDownloadingStatement, mutate: DownloadStatement } = useDownloadStatement({
    onSuccess: (response) => {
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      window.open(url, "_blank");
    },
    onError: async (error) => {
      const responseData = await error?.response?.data?.text();
      dispatchAPIErrorToast({
        message: JSON.parse(responseData ?? "{}")?.error,
        key: "downloadStatement",
      });
    },
  });

  const { minDate, maxDate } = useMemo(() => {
    return { minDate: moment(stakeDebit?.data?.data?.created_date), maxDate: moment().startOf("month") };
  }, [stakeDebit]);

  const handleYearChange = (direction) => {
    setSelectedDate((prev) => {
      const newDate = prev.clone().add(direction, "year");
      return newDate;
    });
  };

  const handleMonthSelect = (date) => {
    setSelectedDate(date);
  };

  const handleViewStatement = () => {
    DownloadStatement({ date: selectedDate.format("YYYY-MM-DD") });
  };

  return isLoadingStakeDebit || !selectedDate ? (
    <ModalSkeleton />
  ) : (
    <ThemeProvider theme={theme}>
      <LocalizationProvider dateAdapter={AdapterMoment} dateFormats={{ monthShort: `MMM${width > 400 ? "M" : ""}` }}>
        <div className="modal-dialog-content">
          <div className="bank-statements-picker">
            <div className="year-navigation">
              <button
                className="nav-button"
                onClick={() => handleYearChange(-1)}
                disabled={selectedDate?.year() <= minDate?.year()}
                aria-label="Previous year"
              >
                <ChevronLeft />
              </button>
              <span className="year-display">{selectedDate?.format("YYYY")}</span>
              <button
                className="nav-button"
                onClick={() => handleYearChange(1)}
                disabled={selectedDate?.year() >= maxDate?.year()}
                aria-label="Next year"
              >
                <ChevronRight />
              </button>
            </div>
            <MonthCalendar
              value={selectedDate}
              onChange={handleMonthSelect}
              minDate={minDate}
              maxDate={maxDate}
              sx={monthCalendarSxProp}
            />
          </div>
        </div>
        <div className="action-container">
          <PrimaryButton onClick={handleViewStatement} disabled={isDownloadingStatement}>
            View Statement {isDownloadingStatement && <CircularProgress />}
          </PrimaryButton>
        </div>
      </LocalizationProvider>
    </ThemeProvider>
  );
};

const monthCalendarSxProp = {
  padding: "0",
  width: "100%",
  gap: "8px",

  "& .MuiPickersMonth-root": {
    flexBasis: "calc(33.33% - 5.33px)",

    "& .MuiPickersMonth-monthButton": {
      font: "normal normal 500 16px/3px Poppins, sans-serif",
      color: "#8B8FA3",
      borderRadius: "8px",
      border: "1px solid #252A3A",
      height: "42px",
      padding: "36px 30px",
      width: "100%",
      margin: "0",
      opacity: 1,

      "&:hover:not(.Mui-selected):not(.Mui-disabled)": {
        backgroundColor: "rgba(255, 255, 255, 0.04)",
      },

      "&.Mui-selected": {
        backgroundColor: "#54FF99",
        color: "#171B26",
        border: "none",
        fontWeight: 600,
      },

      "&.Mui-disabled": {
        borderColor: "rgba(255, 255, 255, 0.08)",
        opacity: 0.4,
      },
    },
  },
};
